/**
 * Main entry point for activQMS React application
 *
 * This file initializes the React application with all necessary providers
 * including routing, state management, authentication, and GraphQL.
 */

import React from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import { Amplify } from "aws-amplify";
import { cognitoUserPoolsTokenProvider } from "aws-amplify/auth/cognito";
import { ApolloProvider } from "@apollo/client";

import App from "./app";
import { apolloClient } from "./services/apollo-client";
import { amplifyConfig } from "./services/amplify-config";
import "./styles/globals.css";
import { AwsRum, AwsRumConfig } from 'aws-rum-web';



try {
  const config: AwsRumConfig = {
    sessionSampleRate: 1,
    endpoint: "https://dataplane.rum.us-east-1.amazonaws.com",
    telemetries: ["performance", "errors", "http"],
    allowCookies: true,
    enableXRay: true, // Enable X-Ray integration for end-to-end tracing
    signing: false // Set to false since no identity pool is configured
  };

  const APPLICATION_ID: string = '43874561-c7bf-4a68-8268-0ded97d2d226';
  const APPLICATION_VERSION: string = '1.0.0';
  const APPLICATION_REGION: string = 'us-east-1';

  const awsRum: AwsRum = new AwsRum(
    APPLICATION_ID,
    APPLICATION_VERSION,
    APPLICATION_REGION,
    config
  );
  console.log("✅ CloudWatch RUM configured successfully:", {
    applicationId: APPLICATION_ID,
    version: APPLICATION_VERSION,
    region: APPLICATION_REGION,
    config: config
  });

  // Test if RUM is working by recording a custom event
  setTimeout(() => {
    try {
      awsRum.recordEvent('app_initialized', {
        timestamp: new Date().toISOString(),
        userAgent: navigator.userAgent
      });
      console.log("✅ RUM test event recorded");
    } catch (error) {
      console.error("❌ Failed to record RUM test event:", error);
    }
  }, 1000);

} catch (error) {
  console.error("Failed to configure CloudWatch RUM:", error);
  // Ignore errors thrown during CloudWatch RUM web client initialization
}

// Configure AWS Amplify
try {
  console.log("Configuring Amplify with:", amplifyConfig);
  Amplify.configure(amplifyConfig);

  // Configure the Cognito token provider
  cognitoUserPoolsTokenProvider.setKeyValueStorage({
    getItem: async (key: string) => localStorage.getItem(key),
    setItem: async (key: string, value: string) =>
      localStorage.setItem(key, value),
    removeItem: async (key: string) => localStorage.removeItem(key),
    clear: async () => localStorage.clear(),
  });

  console.log("Amplify configured successfully");

  // Initialize auth state check after Amplify is configured
  const { useAuthStore } = await import("./stores/auth-store");
  useAuthStore.getState().checkAuthState();
  console.log("Auth state check initiated");
} catch (error) {
  console.error("Failed to configure Amplify:", error);
}

// Get root element
const rootElement = document.getElementById("root");
if (!rootElement) {
  throw new Error("Root element not found");
}

// Create React root and render application
ReactDOM.createRoot(rootElement).render(
  // Temporarily disable StrictMode to test for double-render issues
  // <React.StrictMode>
  <BrowserRouter
    future={{
      v7_startTransition: true,
      v7_relativeSplatPath: true,
    }}
  >
    <ApolloProvider client={apolloClient}>
      <App />
    </ApolloProvider>
  </BrowserRouter>
  // </React.StrictMode>
);
